import { useEffect, useRef } from "react";

// Full-screen night sky drawn on a canvas behind every screen: stars of a few
// sizes and colours twinkle on their own rhythm, drift very slowly, and now and
// then a shooting star crosses. Drawing pauses while the app is in the
// background, and motion is turned off for people who ask for reduced motion.

const COLORS = ["255,255,255", "255,255,255", "255,244,214", "253,224,171", "196,181,253"];

function makeStars(w, h) {
  const count = Math.min(260, Math.round((w * h) / 4200));
  return Array.from({ length: count }, () => {
    const big = Math.random() < 0.08;
    return {
      x: Math.random() * w,
      y: Math.random() * h,
      r: big ? 1.4 + Math.random() * 1.1 : 0.4 + Math.random() * 0.9,
      base: 0.25 + Math.random() * 0.55,
      speed: 0.4 + Math.random() * 1.6,
      phase: Math.random() * Math.PI * 2,
      drift: 0.004 + Math.random() * 0.012,
      color: COLORS[(Math.random() * COLORS.length) | 0],
      glow: big,
    };
  });
}

export default function StarField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let w = 0, h = 0, dpr = 1, stars = [], raf = 0, shooting = null, nextShot = 0, last = 0;

    const resize = () => {
      const widthChanged = window.innerWidth !== w;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // The keyboard only changes the height; keep the same sky so it doesn't jump.
      if (widthChanged || !stars.length) stars = makeStars(w, h);
      if (reduced) start();
    };

    const drawStar = (s, alpha) => {
      if (s.glow) {
        const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r * 5);
        g.addColorStop(0, `rgba(${s.color},${alpha * 0.5})`);
        g.addColorStop(1, `rgba(${s.color},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r * 5, 0, Math.PI * 2);
        ctx.fill();
        // Four-point sparkle on the brightest stars.
        ctx.strokeStyle = `rgba(${s.color},${alpha * 0.45})`;
        ctx.lineWidth = 0.6;
        const len = s.r * 4 * alpha;
        ctx.beginPath();
        ctx.moveTo(s.x - len, s.y); ctx.lineTo(s.x + len, s.y);
        ctx.moveTo(s.x, s.y - len); ctx.lineTo(s.x, s.y + len);
        ctx.stroke();
      }
      ctx.fillStyle = `rgba(${s.color},${alpha})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    };

    const frame = (now) => {
      const t = now / 1000;
      const dt = Math.min(0.05, last ? t - last : 0);
      last = t;
      ctx.clearRect(0, 0, w, h);

      for (const s of stars) {
        const twinkle = reduced ? 1 : 0.55 + 0.45 * Math.sin(t * s.speed + s.phase);
        if (!reduced) {
          s.y -= s.drift * dt * 60;
          if (s.y < -4) { s.y = h + 4; s.x = Math.random() * w; }
        }
        drawStar(s, s.base * twinkle);
      }

      if (!reduced) {
        if (!shooting && t > nextShot) {
          const fromLeft = Math.random() < 0.5;
          shooting = {
            x: fromLeft ? Math.random() * w * 0.5 : w * 0.5 + Math.random() * w * 0.5,
            y: Math.random() * h * 0.4,
            vx: (fromLeft ? 1 : -1) * (500 + Math.random() * 300),
            vy: 180 + Math.random() * 120,
            life: 0,
            ttl: 0.9 + Math.random() * 0.5,
          };
        }
        if (shooting) {
          const s = shooting;
          s.life += dt;
          s.x += s.vx * dt;
          s.y += s.vy * dt;
          const fade = Math.sin(Math.min(1, s.life / s.ttl) * Math.PI);
          const tail = 0.12;
          const grad = ctx.createLinearGradient(s.x, s.y, s.x - s.vx * tail, s.y - s.vy * tail);
          grad.addColorStop(0, `rgba(255,236,190,${0.9 * fade})`);
          grad.addColorStop(1, "rgba(255,236,190,0)");
          ctx.strokeStyle = grad;
          ctx.lineWidth = 1.6;
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(s.x, s.y);
          ctx.lineTo(s.x - s.vx * tail, s.y - s.vy * tail);
          ctx.stroke();
          if (s.life >= s.ttl) {
            shooting = null;
            nextShot = t + 5 + Math.random() * 9;
          }
        }
      }

      if (!reduced) raf = requestAnimationFrame(frame);
    };

    const start = () => {
      cancelAnimationFrame(raf);
      last = 0;
      raf = requestAnimationFrame(frame);
    };
    const onVisibility = () => (document.hidden ? cancelAnimationFrame(raf) : start());

    resize();
    nextShot = performance.now() / 1000 + 3;
    start();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" className="star-field" />;
}
